import { Alert, Button } from '@edgebox/react-components';
import React from 'react';
import { useLocation, useNavigate } from 'react-router';
import { AppParamsContext, IAppParams } from '../../AppParams';
import {
  BRAND_PRESENCE_DEFAULT_PATH,
  BRAND_PRESENCE_ONBOARDING_START_PATH,
  isBrandPresenceBasePath,
  namesBrandPresenceScreen,
} from './brandPresencePaths';
import { useBrandPresenceOnboarding } from './BrandPresenceOnboardingProvider';

/**
 * The element behind the area's bare path, which decides where the area opens.
 *
 * The host page's frame always boots here and the path it asks for arrives afterwards by message, so
 * this element mounts on every load, deep links included, and its answer is only known once the
 * onboarding state has resolved. Two guards keep that answer from taking over a screen the user
 * actually asked for: it navigates only while it is still the matched route, and only when the path
 * the host page asks for names no screen inside the area. Both are stated here rather than left to
 * the route-sync hook in the parent, whose effect runs after this one in the same flush and would
 * otherwise be the only thing standing between a requested screen and a default that fired over it —
 * leaving the user a flash of the wrong screen and two history entries where one belongs. The third guard, over
 * a read that lands after the area is gone, belongs where the read is: the provider that owns it.
 */
export function BrandPresenceIndexRoute() {
  const navigate = useNavigate();
  const location = useLocation();
  const appParams = React.useContext(AppParamsContext) as IAppParams | null;
  const { status, failure, isOnboardingCompleted, retry } = useBrandPresenceOnboarding();

  const requestedPath = appParams?.pageOptions?.brandPresencePath || appParams?.pageOptions?.governancePath;
  const destination = isOnboardingCompleted ? BRAND_PRESENCE_DEFAULT_PATH : BRAND_PRESENCE_ONBOARDING_START_PATH;

  React.useEffect(() => {
    if (status !== 'resolved' || !isBrandPresenceBasePath(location.pathname) || namesBrandPresenceScreen(requestedPath)) {
      return undefined;
    }

    // The host page's query string is the area's own state, and every other redirect in the route
    // table carries it across; this one is no different.
    navigate(`${destination}${location.search}`, { replace: true });

    return undefined;
  }, [status, location.pathname, requestedPath, destination, navigate]);

  if (status === 'failed') {
    return (
      <div className="p-6">
        <Alert variant="danger">
          {failure === 'session-project' ? (
            // No retry: the session and the project in view are both fixed for this frame, so nothing
            // the user can press here would change the answer. Only reopening the area can.
            <span>
              This area was opened for a different project than the one this session is for. Open it again from the project you want.
            </span>
          ) : (
            <div className="d-flex align-items-center gap-3 flex-wrap">
              <span>We could not open this area. Check your connection and try again.</span>
              <Button variant="outline-secondary" size="sm" onClick={retry}>
                Try again
              </Button>
            </div>
          )}
        </Alert>
      </div>
    );
  }

  return (
    <div className="p-6 text-slate-500" role="status">
      Opening Brand Presence...
    </div>
  );
}
